import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, X, Mail, ArrowRight } from "lucide-react";
import { trackEvent, getTrackingMeta } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "bg-white border border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:border-[#1a73e8] focus-visible:ring-2 focus-visible:ring-[#1a73e8]/20 rounded-lg h-12 transition-shadow";

/**
 * DIY platform access modal — captures contact details and triggers
 * a personalised email from thato@rekisa.co.za with the platform link.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - platform: "chatfuel" | "emergent"
 * - platformLabel: string  (e.g. "Chatfuel" / "Emergent")
 * - accent: hex string for the brand accent
 */
export default function DiyAccessModal({ open, onClose, platform, platformLabel, accent = "#1a73e8" }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile_number: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Reset state whenever a new modal is opened
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setForm({ first_name: "", last_name: "", email: "", mobile_number: "" });
    }
  }, [open, platform]);

  if (!open) return null;

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.first_name.trim()) return "First name is required.";
    if (!form.last_name.trim()) return "Last name is required.";
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email)) return "A valid email is required.";
    if (!form.mobile_number.trim()) return "Mobile number is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }

    try {
      setSubmitting(true);
      const payload = {
        platform,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        mobile_number: form.mobile_number.trim(),
        tracking: getTrackingMeta(),
      };
      await axios.post(`${API}/diy-access`, payload);
      trackEvent("diy_access_request_submit", { platform });
      setSubmitted(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="diy-modal-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        data-testid="diy-modal"
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)] overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="diy-modal-close"
          aria-label="Close"
          className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors"
        >
          <X className="h-4 w-4 text-zinc-700" />
        </button>

        {!submitted ? (
          <div className="p-6 sm:p-9">
            <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1 text-[11px] font-mono font-bold uppercase tracking-[0.18em]" style={{ color: accent }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} /> DIY · {platformLabel}
            </div>
            <h2 className="font-display mt-4 text-2xl sm:text-3xl font-black tracking-[-0.02em] text-zinc-900 leading-tight">
              Get your {platformLabel} access link
            </h2>
            <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
              Pop in your details and Thato from Rekisa will email your personalised access link, a quick intro, and tips on what you'll be able to build with {platformLabel}.
            </p>

            <form onSubmit={handleSubmit} data-testid="diy-modal-form" className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-zinc-800">First name <span className="text-[#ea4335]">*</span></Label>
                  <Input data-testid="diy-field-first-name" className={FIELD} value={form.first_name}
                         onChange={(e) => onChange("first_name", e.target.value)} autoComplete="given-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-zinc-800">Surname <span className="text-[#ea4335]">*</span></Label>
                  <Input data-testid="diy-field-last-name" className={FIELD} value={form.last_name}
                         onChange={(e) => onChange("last_name", e.target.value)} autoComplete="family-name" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-zinc-800">Email <span className="text-[#ea4335]">*</span></Label>
                <Input data-testid="diy-field-email" className={FIELD} type="email" value={form.email}
                       onChange={(e) => onChange("email", e.target.value)} autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-zinc-800">Mobile number <span className="text-[#ea4335]">*</span></Label>
                <Input data-testid="diy-field-mobile" className={FIELD} type="tel" inputMode="tel" value={form.mobile_number}
                       onChange={(e) => onChange("mobile_number", e.target.value)} autoComplete="tel" />
              </div>

              <Button type="submit" disabled={submitting} data-testid="diy-modal-submit"
                      className="w-full text-white rounded-full h-12 text-sm font-semibold shadow-lg transition-all"
                      style={{ background: accent }}>
                {submitting
                  ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending your link…</>)
                  : (<>Email me the access link <ArrowRight className="ml-1 h-4 w-4" /></>)}
              </Button>
              <p className="text-center text-[11px] text-zinc-500">
                Your details stay private. We only use them to send your access link and a short intro.
              </p>
            </form>
          </div>
        ) : (
          <div className="p-9 text-center" data-testid="diy-modal-success">
            <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center" style={{ background: `${accent}1a` }}>
              <Mail className="h-7 w-7" style={{ color: accent }} />
            </div>
            <h3 className="font-display mt-5 text-2xl font-black text-zinc-900">Check your inbox in a minute.</h3>
            <p className="mt-2 text-sm text-zinc-600 leading-relaxed max-w-sm mx-auto">
              Thato has just sent your personalised access link from <span className="font-mono font-bold text-zinc-800">thato@rekisa.co.za</span> with a short intro on getting started with {platformLabel}.
            </p>
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-zinc-500">
              <CheckCircle2 className="h-3.5 w-3.5 text-[#34a853]" /> Sent to {form.email || "your email"}
            </div>
            <Button onClick={onClose} data-testid="diy-modal-done" className="mt-6 rounded-full px-6 h-11 bg-zinc-900 hover:bg-black text-white">Done</Button>
          </div>
        )}
      </div>
    </div>
  );
}
